import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate  } from 'react-router-dom';
import { Lock, User, Eye, EyeOff, Shield, CheckCircle, ArrowLeft, AlertCircle } from 'lucide-react';

const AdminLoginForm = () => {
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [currentView, setCurrentView] = useState('login'); // 'login', 'loading', 'success', 'error'
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Credenciales de prueba
  const validCredentials = {
    'admin': 'admin123',
    'supervisor': 'super456',
    'gerente': 'manager789'
  };

  const handleInputChange = (field, value) => {
    setCredentials({...credentials, [field]: value});
    if (error) setError(''); // Limpiar error al escribir
  };

  const handleLogin = () => {
    setError('');
    
    // Validaciones básicas
    if (!credentials.username || !credentials.password) {
      setError('Por favor completa todos los campos');
      return;
    }

    setCurrentView('loading');

    // Simular verificación de credenciales
    setTimeout(() => {
      const isValid = validCredentials[credentials.username.toLowerCase()] === credentials.password;
      
      if (isValid) {
        setCurrentView('success');
        // Redirección automática después de 3 segundos
        setTimeout(() => {
          navigate('/dashboard');
        }, 3000);
      } else {
        setError('Usuario o contraseña incorrectos');
        setCurrentView('error');
        
        // Volver al login después de mostrar error
        setTimeout(() => {
          setCurrentView('login');
        }, 2000);
      }
    }, 2000);
  };

  const resetForm = () => {
    setCredentials({ username: '', password: '' });
    setError('');
    setCurrentView('login');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

   const handleDashboardNavigation = () => {
    navigate('/dashboard');
  };

  const handleLoginNavigation = () => {
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <div className="pt-24 pb-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Botón de volver */}
          <div className="flex justify-start mb-12">
            <Link
              to="/"
              className="flex items-center text-gray-700 hover:text-pink-500 transition-colors bg-white px-4 py-2 rounded-lg shadow-md hover:shadow-lg"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Inicio
            </Link>
          </div>

          <AnimatePresence mode="wait">
            {/* Vista de Login */}
            {currentView === 'login' && (
              <motion.div
                key="login"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="max-w-md mx-auto"
              >
                <div className="text-center mb-8">
                  <motion.div
                    className="w-24 h-24 bg-gradient-to-r from-blue-600 to-indigo-700 rounded-full flex items-center justify-center mx-auto mb-6 shadow-2xl"
                    whileHover={{ rotate: 360, scale: 1.1 }}
                    transition={{ duration: 0.6 }}
                  >
                    <Shield className="w-12 h-12 text-white" />
                  </motion.div>

                  <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4 bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                    Acceso Admin
                  </h1>

                  <p className="text-lg text-gray-600">
                    Ingresa tus credenciales para acceder al panel administrativo
                  </p>
                </div>

                <div className="bg-white rounded-2xl shadow-xl p-8">
                  <div className="space-y-6">
                    {/* Campo Usuario */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Usuario
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <User className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="text"
                          value={credentials.username}
                          onChange={(e) => handleInputChange('username', e.target.value)}
                          onKeyPress={handleKeyPress}
                          placeholder="Ingresa tu usuario"
                          className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                        />
                      </div>
                    </div>

                    {/* Campo Contraseña */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Contraseña
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Lock className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={credentials.password}
                          onChange={(e) => handleInputChange('password', e.target.value)}
                          onKeyPress={handleKeyPress}
                          placeholder="Ingresa tu contraseña"
                          className="block w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        >
                          {showPassword ? (
                            <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                          ) : (
                            <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Mensaje de Error */}
                    {error && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center"
                      >
                        <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
                        <span className="text-red-700 text-sm">{error}</span>
                      </motion.div>
                    )}

                    {/* Botón de Login */}
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleLogin}
                      disabled={!credentials.username || !credentials.password}
                      className="w-full bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-3 px-6 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg transition-all duration-300 flex items-center justify-center"
                    >
                      <Shield className="w-5 h-5 mr-2" />
                      Iniciar Sesión
                    </motion.button>
                  </div>
                </div>

                {/* Información de prueba */}
                <div className="mt-8 bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium text-gray-700 mb-2">Credenciales de prueba:</h3>
                  <div className="text-sm text-gray-600 space-y-1">
                    <p><strong>admin</strong> / admin123</p>
                    <p><strong>supervisor</strong> / super456</p>
                    <p><strong>gerente</strong> / manager789</p>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Vista de Cargando */}
            {currentView === 'loading' && (
              <motion.div
                key="loading"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="text-center max-w-md mx-auto"
              >
                <motion.div
                  className="w-24 h-24 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-8 shadow-2xl"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                >
                  <Lock className="w-12 h-12 text-white" />
                </motion.div>

                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                  Verificando Credenciales...
                </h2>

                <div className="bg-white rounded-2xl shadow-xl p-8">
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-3 h-3 bg-blue-500 rounded-full animate-bounce"></div>
                    <div className="w-3 h-3 bg-blue-500 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                    <div className="w-3 h-3 bg-blue-500 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                  </div>
                  <p className="text-gray-600 mt-4">Por favor espera...</p>
                </div>
              </motion.div>
            )}

            {/* Vista de Error */}
            {currentView === 'error' && (
              <motion.div
                key="error"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="text-center max-w-md mx-auto"
              >
                <motion.div
                  className="w-24 h-24 bg-gradient-to-r from-red-500 to-red-600 rounded-full flex items-center justify-center mx-auto mb-8 shadow-2xl"
                  initial={{ scale: 0 }}
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 0.5, times: [0, 0.5, 1] }}
                >
                  <AlertCircle className="w-12 h-12 text-white" />
                </motion.div>

                <h2 className="text-3xl font-bold text-gray-800 mb-4 bg-gradient-to-r from-red-600 to-red-700 bg-clip-text text-transparent">
                  Acceso Denegado
                </h2>

                <div className="bg-white rounded-2xl shadow-xl p-8">
                  <p className="text-red-600 text-lg mb-4">{error}</p>
                  <p className="text-gray-600">Serás redirigido al formulario en unos segundos...</p>
                </div>
              </motion.div>
            )}

            {/* Vista de Éxito */}
            {currentView === 'success' && (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="text-center max-w-2xl mx-auto"
              >
                <motion.div
                  className="w-32 h-32 bg-gradient-to-r from-green-400 to-green-600 rounded-full flex items-center justify-center mx-auto mb-8 shadow-2xl"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                >
                  <CheckCircle className="w-16 h-16 text-white" />
                </motion.div>

                <h2 className="text-4xl font-bold text-gray-800 mb-4 bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">
                  ¡Acceso Autorizado!
                </h2>

                <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
                  <p className="text-xl text-gray-600 mb-6">
                    Bienvenido al panel administrativo, <span className="font-bold text-blue-600">{credentials.username}</span>
                  </p>
                  
                  <div className="bg-green-50 rounded-lg p-4 mb-6">
                    <h3 className="font-bold text-green-800 mb-2">Sesión Iniciada:</h3>
                    <p className="text-green-700">Usuario: {credentials.username}</p>
                    <p className="text-green-700">Hora: {new Date().toLocaleString()}</p>
                    <p className="text-green-700">Estado: Autenticado</p>
                  </div>

                  <p className="text-sm text-gray-500">
                    Redirigiendo al panel de administración en 3 segundos...
                  </p>
                </div>

                <div className="flex space-x-4">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleDashboardNavigation}
                    className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-8 py-4 rounded-full text-lg font-semibold shadow-xl hover:shadow-2xl transition-all duration-300"
                  >
                    Ir al Dashboard
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleLoginNavigation}
                    className="flex-1 bg-gradient-to-r from-gray-500 to-gray-600 text-white px-8 py-4 rounded-full text-lg font-semibold shadow-xl hover:shadow-2xl transition-all duration-300"
                  >
                    Nuevo Login
                  </motion.button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginForm;